import { useState } from 'react';
import { QuoteBusinessSourceEnum } from '@jove-frontend/enums';
import { getLocalTranslation, isPublishOnline, useLocalStorage } from '@jove-frontend/uikit';

export enum QuoteListTableColumnFilterEnum {
  id = 'id',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
  customerName = 'firstName',
  email = 'email',
  phone = 'phone',
  insuranceType = 'insuranceType',
  assignee = 'assigneeId',
  insurer = 'providerCode',
  purchaseProbability = 'purchaseProbability',
  companyName = 'companyName',
  region = 'region',
  status = 'status',
  copyURL = 'copyURL',
  policyNumber = 'policyNumber',
  aggregatePrice = 'aggregate_price',
  renewalStatus = 'renewalStatus',
  organizationName = 'organizationName',
  emailOpenRate = 'emailOpenRate',
  emailClickRate = 'emailClickRate',
  quoteNumber = 'quoteNumber',
  contractStartDate = 'contractStartDate',
  propertyAnalysisNotAvailable = 'property_analysis_not_available',
  sanctionCheckReferral = 'sanction_check_referral',
  partnerName = 'partnerName',
  partnerReferralFee = 'partnerReferralFee',
  partnerFee = 'partnerFee',
  brokerName = 'brokerName',
  duplicateQuote = 'duplicateQuote',
  policyExpiryDate = 'policy_expiry_date',
  policyExpirationDays = 'policyExpirationDays',
  policyStartDate = 'policy_start_date',
  validationStatus = 'validation_status',
  source = 'source',
  quoteCreationMethod = 'quote_creation_method',
  purchasedAt = 'purchased_at',
  joveAssigneeId = 'jove_assignee_d',
  referralAssigneeId = 'referral_assignee_id',
  originationManager = 'origination_manager',
  activationManager = 'activation_manager',
}
export const quoteListTableColumnFilterList = (config?: { sourceType?: QuoteBusinessSourceEnum }) => {
  const { $t } = getLocalTranslation();
  const list = [
    { value: QuoteListTableColumnFilterEnum.id, label: $t('common:id'), checked: false },
    { value: QuoteListTableColumnFilterEnum.createdAt, label: $t('common:created'), checked: false, sortable: true },
    { value: QuoteListTableColumnFilterEnum.updatedAt, label: $t('common:updated'), checked: true, sortable: true },
    { value: QuoteListTableColumnFilterEnum.customerName, label: $t('common:customer_name'), checked: true },
    { value: QuoteListTableColumnFilterEnum.email, label: $t('common:email'), checked: false },
    { value: QuoteListTableColumnFilterEnum.phone, label: 'Customer phone number', checked: true },
    {
      value: QuoteListTableColumnFilterEnum.insuranceType,
      label: $t('common:insurance_type'),
      checked: true,
    },
    {
      value: QuoteListTableColumnFilterEnum.assignee,
      label: [QuoteBusinessSourceEnum.broker, QuoteBusinessSourceEnum.brokerQuote, QuoteBusinessSourceEnum.brokerDetailInfoView].includes(
        config?.sourceType
      )
        ? $t('page:quoteListTableColumnFilter.broker_assignee')
        : $t('common:assignee'),
      checked: true,
    },
    { value: QuoteListTableColumnFilterEnum.insurer, label: $t('common:insurer'), checked: true },
    {
      value: QuoteListTableColumnFilterEnum.purchaseProbability,
      label: $t('common:purchase_probability'),
      checked: true,
    },
    {
      value: QuoteListTableColumnFilterEnum.companyName,
      label: $t('common:company_name'),
      checked: true,
    },
    {
      value: QuoteListTableColumnFilterEnum.region,
      label: $t('common:region'),
      checked: true,
    },
    { value: QuoteListTableColumnFilterEnum.status, label: $t('common:status'), checked: true },
    { value: QuoteListTableColumnFilterEnum.copyURL, label: $t('common:copy_URL'), checked: true },
    { value: QuoteListTableColumnFilterEnum.policyNumber, label: $t('common:policy_number'), checked: true },
    {
      value: QuoteListTableColumnFilterEnum.aggregatePrice,
      label: $t('common:aggregate_price'),
      checked: true,
      sortable: true,
    },
    {
      value: QuoteListTableColumnFilterEnum.renewalStatus,
      label: $t('common:renewal_status'),
      checked: true,
    },
    {
      value: QuoteListTableColumnFilterEnum.organizationName,
      label: $t('common:organization_name'),
      checked: false,
    },
    {
      value: QuoteListTableColumnFilterEnum.emailOpenRate,
      label: $t('common:email_open_rate'),
      checked: false,
    },
    {
      value: QuoteListTableColumnFilterEnum.emailClickRate,
      label: $t('common:email_click_rate'),
      checked: false,
    },
    {
      value: QuoteListTableColumnFilterEnum.quoteNumber,
      label: $t('common:quote_number'),
      checked: false,
    },
    {
      value: QuoteListTableColumnFilterEnum.contractStartDate,
      label: $t('common:contract_start_date'),
      checked: false,
    },
  ];

  if ([QuoteBusinessSourceEnum.quote].includes(config?.sourceType)) {
    list.push(
      ...[
        {
          value: QuoteListTableColumnFilterEnum.duplicateQuote,
          label: $t('page:quoteListTableColumnFilter.duplicate_quote'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.policyExpiryDate,
          label: $t('page:quoteListTableColumnFilter.policy_document_expiry_date'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.policyExpirationDays,
          label: $t('page:quoteListTableColumnFilter.policy_document_expiration_days'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.policyStartDate,
          label: $t('common:policy_start_date'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.validationStatus,
          label: $t('page:quoteListTableColumnFilter.validation_status'),
          checked: false,
        },
      ]
    );
    if (!isPublishOnline()) {
      list.push(
        ...[
          {
            value: QuoteListTableColumnFilterEnum.propertyAnalysisNotAvailable,
            label: 'Property analysis not available',
            checked: false,
          },
          {
            value: QuoteListTableColumnFilterEnum.sanctionCheckReferral,
            label: 'Sanction check referral',
            checked: false,
          },
        ]
      );
    }
  }

  list.push(
    ...[
      {
        value: QuoteListTableColumnFilterEnum.source,
        label: $t('page:quoteListTableColumnFilter.source'),
        checked: false,
      },
      {
        value: QuoteListTableColumnFilterEnum.purchasedAt,
        label: $t('page:quoteListTableColumnFilter.purchased_at'),
        checked: false,
        sortable: true,
      },
      {
        value: QuoteListTableColumnFilterEnum.referralAssigneeId,
        label: $t('page:quoteListTableColumnFilter.referral_assignee'),
        checked: false,
      },
    ]
  );

  if (!config?.sourceType || [QuoteBusinessSourceEnum.quote, QuoteBusinessSourceEnum.partner].includes(config?.sourceType)) {
    list.push(
      ...[
        {
          value: QuoteListTableColumnFilterEnum.partnerName,
          label: $t('pages:partners.partner_name'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.partnerReferralFee,
          label: $t('pages:partners.partner_referral_fee'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.partnerFee,
          label: $t('pages:quotes.partner_fee'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.quoteCreationMethod,
          label: $t('page:quoteListTableColumnFilter.quote_creation_methods'),
          checked: false,
        },
      ]
    );
  }

  if ([QuoteBusinessSourceEnum.brokerQuote, QuoteBusinessSourceEnum.brokerDetailInfoView].includes(config?.sourceType)) {
    list.push(
      ...[
        {
          value: QuoteListTableColumnFilterEnum.brokerName,
          label: $t('pages:brokers.broker_name'),
          checked: false,
        },
        {
          value: QuoteListTableColumnFilterEnum.joveAssigneeId,
          label: $t('page:quoteListTableColumnFilter.jove_assignee'),
          checked: false,
        },
      ]
    );
    if (!isPublishOnline()) {
      list.push(
        ...[
          {
            value: QuoteListTableColumnFilterEnum.originationManager,
            label: $t('page:quoteListFilter.origination_manager'),
            checked: false,
          },
          {
            value: QuoteListTableColumnFilterEnum.activationManager,
            label: $t('page:quoteListFilter.activation_manager'),
            checked: false,
          },
        ]
      );
    }
  }

  return list;
};

export const quoteListTableColumnFilterOriginalSaveKey = 'quote-list-table-filter';
export const quoteListTableColumnFilterSaveKey = 'quote-list-table-filter-1';

export const useQuoteListTableColumnFilter = () => {
  const [initialValue, setInitialValue] = useLocalStorage(quoteListTableColumnFilterOriginalSaveKey, undefined);
  const [newValue, setNewValue] = useLocalStorage(quoteListTableColumnFilterSaveKey, undefined);
  const [showColumList, setShowColumList] = useState(newValue || initialValue || quoteListTableColumnFilterList());

  return { showColumList, setShowColumList };
};
